"use client"

import {
  ArtifactFormSkeleton,
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasSaveActions,
  Badge,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useOrganisations,
} from '@yeshuman/sdk'
import type { Organisation, BriefDraftPrefill } from '@yeshuman/sdk'
import { useEffect, useMemo, useState } from 'react'
import { Building2, X } from 'lucide-react'
import { toast } from 'sonner'

import {
  type Brief,
  type CreateBriefData,
  type UpdateBriefData,
  useCreateBrief,
  useUpdateBrief,
} from '../../hooks/use-briefs'
import { useSkills } from '../../hooks/use-skills'

const EMPTY_ORGANISATIONS: Organisation[] = []
const FORM_ID = 'brief-canvas-form'

export type BriefCanvasFormProps = {
  mode: 'create' | 'edit'
  brief?: Brief
  initialDraft?: BriefDraftPrefill
  onClose: () => void
  onSaved: (brief: Brief) => void
}

export function BriefCanvasForm({
  mode,
  brief,
  initialDraft,
  onClose,
  onSaved,
}: BriefCanvasFormProps) {
  const isEditing = mode === 'edit'
  const createDraft = mode === 'create' ? initialDraft : undefined
  const { createBrief, isCreating } = useCreateBrief()
  const { updateBrief, isUpdating } = useUpdateBrief()
  const { skills } = useSkills()
  const { organisations: organisationsData } = useOrganisations()

  const organisations = useMemo(() => organisationsData ?? EMPTY_ORGANISATIONS, [organisationsData])

  const [title, setTitle] = useState(
    brief?.title || createDraft?.title || createDraft?.titleSuggestion || '',
  )
  const [description, setDescription] = useState(
    brief?.description || createDraft?.description || '',
  )
  const [location, setLocation] = useState(brief?.location || createDraft?.location || '')
  const [instructions, setInstructions] = useState(
    brief?.instructions || createDraft?.instructions || '',
  )
  const [status, setStatus] = useState<'draft' | 'active' | 'closed'>(
    brief?.status || createDraft?.status || 'draft',
  )
  const [priority, setPriority] = useState<'high' | 'medium' | 'low'>(
    brief?.priority || createDraft?.priority || 'medium',
  )
  const [clientOrganisationId, setClientOrganisationId] = useState(
    brief?.client_organisation_id || createDraft?.client_organisation_id || '',
  )

  const [requiredSkills, setRequiredSkills] = useState<string[]>(
    brief?.skills?.filter((s) => s.requirement_type === 'required').map((s) => s.skill_name) ||
      createDraft?.required_skills ||
      [],
  )
  const [preferredSkills, setPreferredSkills] = useState<string[]>(
    brief?.skills?.filter((s) => s.requirement_type === 'preferred').map((s) => s.skill_name) ||
      createDraft?.preferred_skills ||
      [],
  )

  const [skillInput, setSkillInput] = useState('')
  const [skillType, setSkillType] = useState<'required' | 'preferred'>('required')
  const [filteredSkills, setFilteredSkills] = useState<string[]>([])

  const statusValue = status === 'draft' || status === 'active' || status === 'closed' ? status : 'draft'
  const priorityValue = priority === 'high' || priority === 'medium' || priority === 'low' ? priority : 'medium'

  const clientOrgSelectValue = useMemo(() => {
    if (!clientOrganisationId) return 'none'
    const orgExists = organisations.some((org) => org.id === clientOrganisationId)
    return orgExists ? clientOrganisationId : 'none'
  }, [clientOrganisationId, organisations])

  const organisationOptions = useMemo(
    () =>
      organisations.map((org) => (
        <SelectItem key={org.id} value={org.id}>
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            {org.name}
          </div>
        </SelectItem>
      )),
    [organisations],
  )

  useEffect(() => {
    if (skillInput.length > 0) {
      const filtered = skills
        .map((s) => s.name)
        .filter(
          (name) =>
            name.toLowerCase().includes(skillInput.toLowerCase()) &&
            !requiredSkills.includes(name) &&
            !preferredSkills.includes(name),
        )
        .slice(0, 10)
      setFilteredSkills(filtered)
    } else {
      setFilteredSkills([])
    }
  }, [skillInput, skills, requiredSkills, preferredSkills])

  const handleAddSkill = (skillName: string) => {
    if (skillType === 'required') {
      if (!requiredSkills.includes(skillName)) {
        setRequiredSkills([...requiredSkills, skillName])
      }
    } else if (!preferredSkills.includes(skillName)) {
      setPreferredSkills([...preferredSkills, skillName])
    }
    setSkillInput('')
    setFilteredSkills([])
  }

  const handleRemoveRequiredSkill = (skillName: string) => {
    setRequiredSkills(requiredSkills.filter((s) => s !== skillName))
  }

  const handleRemovePreferredSkill = (skillName: string) => {
    setPreferredSkills(preferredSkills.filter((s) => s !== skillName))
  }

  const buildPayload = (): CreateBriefData | UpdateBriefData => ({
    title: title.trim(),
    description: description.trim() || undefined,
    location: location.trim() || undefined,
    instructions: instructions.trim() || undefined,
    status,
    priority,
    client_organisation_id: clientOrganisationId.trim() || undefined,
    required_skills: requiredSkills.length > 0 ? requiredSkills : undefined,
    preferred_skills: preferredSkills.length > 0 ? preferredSkills : undefined,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!title.trim()) {
      toast.error('Title is required')
      return
    }

    const data = buildPayload()

    if (isEditing && brief) {
      updateBrief(
        { id: brief.id, data: data as UpdateBriefData },
        {
          onSuccess: (updated) => {
            toast.success('Brief updated')
            onSaved(updated)
          },
          onError: (error: Error) => {
            toast.error(error.message || 'Failed to update brief')
          },
        },
      )
      return
    }

    createBrief(data as CreateBriefData, {
      onSuccess: (created) => {
        toast.success('Brief created')
        onSaved(created)
      },
      onError: (error: Error) => {
        toast.error(error.message || 'Failed to create brief')
      },
    })
  }

  const isSaving = isCreating || isUpdating

  if (isEditing && !brief) {
    return (
      <ArtifactCanvasBody className="min-h-0 flex-1 overflow-y-auto">
        <ArtifactFormSkeleton fields={5} ariaLabel="Loading brief" />
      </ArtifactCanvasBody>
    )
  }

  return (
    <>
      <ArtifactCanvasBody className="min-h-0 flex-1 overflow-y-auto">
        <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor={`${FORM_ID}-title`}>Title *</Label>
            <Input
              id={`${FORM_ID}-title`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Senior Python Developer"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${FORM_ID}-description`}>Description</Label>
            <Textarea
              id={`${FORM_ID}-description`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the role…"
              rows={4}
              className="placeholder:italic"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${FORM_ID}-location`}>Location</Label>
            <Input
              id={`${FORM_ID}-location`}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., Remote, London, UK"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${FORM_ID}-instructions`}>Evaluation instructions</Label>
            <p className="text-sm text-muted-foreground">
              Guide the AI on what to prioritize when evaluating candidates.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setInstructions(
                    `Focus on hands-on technical experience with the required skills.
Recent coding experience is essential - management-only roles may not fit.
Consider open source contributions or portfolio projects as strong signals.`,
                  )
                }
              >
                Technical role
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setInstructions(
                    `Prioritize leadership and team management experience.
Look for candidates who have grown or scaled teams.
Track record of mentoring and developing talent is important.`,
                  )
                }
              >
                Leadership role
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setInstructions(
                    `This client values [describe culture].
Look for indicators of: [key traits].
Red flags to watch for: [concerns].`,
                  )
                }
              >
                Culture fit
              </Button>
            </div>
            <Textarea
              id={`${FORM_ID}-instructions`}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="E.g., Focus on Python experience. Prefer candidates from fintech…"
              rows={6}
              className="placeholder:italic"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`${FORM_ID}-status`}>Status</Label>
              <Select
                value={statusValue}
                onValueChange={(value: 'draft' | 'active' | 'closed') => setStatus(value)}
              >
                <SelectTrigger id={`${FORM_ID}-status`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${FORM_ID}-priority`}>Priority</Label>
              <Select
                value={priorityValue}
                onValueChange={(value: 'high' | 'medium' | 'low') => setPriority(value)}
              >
                <SelectTrigger id={`${FORM_ID}-priority`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${FORM_ID}-client-org`}>Client organisation</Label>
            <Select
              value={clientOrgSelectValue}
              onValueChange={(value) => {
                setClientOrganisationId(value === 'none' ? '' : value)
              }}
            >
              <SelectTrigger id={`${FORM_ID}-client-org`}>
                <SelectValue placeholder="Select an organisation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    No organisation
                  </div>
                </SelectItem>
                {organisationOptions}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`${FORM_ID}-skill-input`}>Skills</Label>
              <div className="flex gap-2">
                <Input
                  id={`${FORM_ID}-skill-input`}
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  placeholder="Type to search skills…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && filteredSkills.length > 0) {
                      e.preventDefault()
                      handleAddSkill(filteredSkills[0])
                    }
                  }}
                />
                <Select
                  value={skillType}
                  onValueChange={(value: 'required' | 'preferred') => setSkillType(value)}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="required">Required</SelectItem>
                    <SelectItem value="preferred">Preferred</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {filteredSkills.length > 0 ? (
                <div className="mt-2 max-h-32 overflow-y-auto rounded-md border p-4">
                  {filteredSkills.map((skill) => (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => handleAddSkill(skill)}
                      className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-muted"
                    >
                      {skill}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {requiredSkills.length > 0 ? (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Required skills</Label>
                <div className="flex flex-wrap gap-2">
                  {requiredSkills.map((skill) => (
                    <Badge key={skill} variant="default" className="flex items-center gap-1">
                      {skill}
                      <button
                        type="button"
                        onClick={() => handleRemoveRequiredSkill(skill)}
                        className="ml-1 hover:text-destructive"
                        aria-label={`Remove ${skill}`}
                      >
                        <X className="size-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}

            {preferredSkills.length > 0 ? (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Preferred skills</Label>
                <div className="flex flex-wrap gap-2">
                  {preferredSkills.map((skill) => (
                    <Badge key={skill} variant="outline" className="flex items-center gap-1">
                      {skill}
                      <button
                        type="button"
                        onClick={() => handleRemovePreferredSkill(skill)}
                        className="ml-1 hover:text-destructive"
                        aria-label={`Remove ${skill}`}
                      >
                        <X className="size-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </form>
      </ArtifactCanvasBody>

      <ArtifactCanvasFooterBar
        footerClassName="shrink-0"
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={() =>
              (document.getElementById(FORM_ID) as HTMLFormElement | null)?.requestSubmit()
            }
            isDirty
            isSaving={isSaving}
            saveLabel={isEditing ? 'Save brief' : 'Create brief'}
            savingLabel="Saving…"
            closeDisabled={isSaving}
          />
        }
      />
    </>
  )
}
